// Aesthetic: Utilitarian holding list - slate, tabular numbers, state carried
// by a left border so the shape of the backlog reads at a glance. Emerald only
// on the act of attaching.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import { RefreshCw, Inbox, Loader2, Paperclip } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'react-toastify';
import { cn } from '../lib/utils';
import AttachCard from './AttachCard';

const prodBase = process.env.REACT_APP_API_BASE;
const attachQueueRoute = process.env.REACT_APP_ATTACH_QUEUE || '/api/attachqueue';
const attachmentRoute = process.env.REACT_APP_ATTACHMENT || '/api/attachment';
const grabInvoicesRoute = process.env.REACT_APP_GRAB_INVOICES || '/api/grabinvoices';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'matched', label: 'Ready to attach' },
  { key: 'waiting', label: 'Waiting on the invoice' },
  { key: 'unmatched', label: 'Needs a match' }
];

/**
 * Packing slips that have been scanned but not yet placed on a voucher.
 *
 * Nothing here ages out. A slip stays until it is attached, however long its
 * invoice takes - most of them arrive before the invoice they belong to has
 * even been filed, so "waiting" is the ordinary state, not a problem to fix.
 */
export function AttachQueue({ onAttached, useremail }) {
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [busyKeys, setBusyKeys] = useState(() => new Set());
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // No limit: the endpoint is uncapped now, and asking for 500 would cap a
      // queue that regularly runs past it.
      const res = await fetch(`${prodBase}${attachQueueRoute}`);
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.details || data.error || `Failed (${res.status})`);
      }
      setSlips(Array.isArray(data.slips) ? data.slips : []);
    } catch (err) {
      toast.error(`Could not load packing slips: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(
    () =>
      slips.reduce((acc, s) => {
        acc[s.state] = (acc[s.state] || 0) + 1;
        return acc;
      }, {}),
    [slips]
  );

  const visible = useMemo(
    () => (filter === 'all' ? slips : slips.filter((s) => s.state === filter)),
    [slips, filter]
  );

  const setBusy = (key, on) =>
    setBusyKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const handleAttach = useCallback(
    async (slip, target, transform) => {
      if (!target?.id) return;
      setBusy(slip.key, true);
      try {
        const res = await fetch(`${prodBase}${attachmentRoute}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'attach',
            documentId: slip.documentId,
            segmentIndex: slip.segmentIndex,
            targetItemId: target.id,
            targetFileName: target.name,
            transform,
            reviewer: useremail || 'unknown'
          })
        });
        const data = await res.json();
        if (!res.ok || data.success === false) {
          throw new Error(data.details || data.error || `Failed (${res.status})`);
        }

        toast.success(
          `Attached to ${data.targetFileName || target.name} - now ${data.pageCount} pages`
        );
        // Drop it from the list rather than refetching: the row is finished, and
        // a full reload would scroll the clerk away from where they were.
        setSlips((prev) => prev.filter((s) => s.key !== slip.key));
        onAttached?.();
      } catch (err) {
        toast.error(`Could not attach: ${err.message}`);
      } finally {
        setBusy(slip.key, false);
      }
    },
    [onAttached, useremail]
  );

  /**
   * The clerk chooses between the invoices an order was billed as.
   *
   * Resolves the voucher to its archived file so the card can show it, rather
   * than attaching straight away - the whole point of this tab is that both
   * documents are seen before anything is written.
   */
  const handlePickCandidate = useCallback(async (slip, candidate) => {
    setBusy(slip.key, true);
    try {
      const res = await fetch(
        `${prodBase}${grabInvoicesRoute}?q=${encodeURIComponent('V' + candidate.voucherNumber)}`
      );
      const data = await res.json();
      const prefix = `V${candidate.voucherNumber}_`.toUpperCase();
      const file = (data.files || []).find((f) => f.name.toUpperCase().startsWith(prefix));

      if (!file) {
        toast.info(
          `V${candidate.voucherNumber} has not been archived yet. Leave the slip here - it will attach itself when that invoice is filed.`
        );
        return;
      }

      setSlips((prev) =>
        prev.map((s) =>
          s.key === slip.key
            ? {
                ...s,
                state: 'matched',
                voucherNumber: candidate.voucherNumber,
                timsInvoiceNumber: candidate.invoiceNumber,
                timsInvoiceDate: candidate.invoiceDate,
                targetItemId: file.id,
                targetFileName: file.name,
                candidateVouchers: null,
                reason: null
              }
            : s
        )
      );
    } catch (err) {
      toast.error(`Could not open V${candidate.voucherNumber}: ${err.message}`);
    } finally {
      setBusy(slip.key, false);
    }
  }, []);

  /**
   * "This is an invoice after all."
   *
   * Nothing was thrown away when it came here - the index proposal the reader
   * made is still on the segment - so this puts back exactly what was there
   * before, rather than sending the clerk to key it in from scratch.
   */
  const handleSendToReview = useCallback(
    async (slip) => {
      setBusy(slip.key, true);
      try {
        const res = await fetch(`${prodBase}${attachmentRoute}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'reclassify',
            documentType: 'invoice',
            documentId: slip.documentId,
            segmentIndex: slip.segmentIndex
          })
        });
        const data = await res.json();
        if (!res.ok || data.success === false) {
          throw new Error(data.details || data.error || `Failed (${res.status})`);
        }
        toast.success('Sent back to the Review Queue');
        setSlips((prev) => prev.filter((s) => s.key !== slip.key));
        onAttached?.();
      } catch (err) {
        toast.error(`Could not send it back: ${err.message}`);
      } finally {
        setBusy(slip.key, false);
      }
    },
    [onAttached]
  );

  const handleRecheck = useCallback(
    async (slip) => {
      const key = slip?.key;
      if (key) setBusy(key, true);
      else setRechecking(true);
      try {
        const res = await fetch(`${prodBase}${attachmentRoute}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'recheck',
            ...(slip ? { documentId: slip.documentId } : {})
          })
        });
        const data = await res.json();
        if (!res.ok || data.success === false) {
          throw new Error(data.details || data.error || `Failed (${res.status})`);
        }
        await load();
        if (!slip) toast.success(`Re-checked ${data.checked} document(s)`);
      } catch (err) {
        toast.error(`Could not re-check: ${err.message}`);
      } finally {
        if (key) setBusy(key, false);
        else setRechecking(false);
      }
    },
    [load]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Paperclip className="h-5 w-5 text-blue-600" />
        <h3 className="font-semibold">
          Packing slips ({slips.length})
        </h3>

        <div className="ml-2 flex flex-wrap gap-1">
          {FILTERS.map((f) => {
            const n = f.key === 'all' ? slips.length : counts[f.key] || 0;
            if (f.key !== 'all' && n === 0) return null;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                aria-pressed={filter === f.key}
                className={cn(
                  'rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
                  filter === f.key
                    ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                )}
              >
                {f.label}
                <span className="ml-1 tabular-nums opacity-70">{n}</span>
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={() => handleRecheck(null)}
            disabled={rechecking || loading}
            title="Look every unplaced slip up again. The index grows as invoices are filed, so a slip that found nothing before may match now."
          >
            {rechecking ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
            )}
            Re-check all
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 text-xs"
            onClick={load}
            disabled={loading}
            aria-label="Reload the list"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {loading && slips.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading packing slips...
        </div>
      ) : slips.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center">
          <Inbox className="h-8 w-8 text-slate-300 dark:text-slate-700" />
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            No packing slips waiting
          </p>
          <p className="max-w-md text-xs text-slate-500 dark:text-slate-400">
            Scan them like anything else. They are recognised automatically and land here,
            matched to the voucher their purchase-order number points at.
          </p>
        </div>
      ) : visible.length === 0 ? (
        <p className="py-8 text-center text-xs text-slate-500 dark:text-slate-400">
          Nothing in this group.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <AnimatePresence initial={false}>
            {visible.map((slip) => (
              <AttachCard
                key={slip.key}
                slip={slip}
                busy={busyKeys.has(slip.key)}
                onAttach={handleAttach}
                onRecheck={handleRecheck}
                onPickCandidate={handlePickCandidate}
                onSendToReview={handleSendToReview}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export default AttachQueue;
